// Port of ios/Sources/ViewModels/QuickSwitcherPresentation.swift.
//
// The current workspace's quick conversation switcher, derived entirely from the cached sidebar
// snapshot. Store reads stay outside this object; filtering on every keystroke is therefore an
// in-memory operation and never enters SQLite or the network path.
import type { ChannelRecord } from './records';
import { swiftStringLess } from './channelListDerivation';

export interface QuickSwitcherRow {
  /** Also the row's identity (Swift's `id`). */
  channelId: string;
  name: string;
  slug: string;
  type: string;
  lastPostAt: number;
}

export function isPrivate(row: QuickSwitcherRow): boolean {
  return row.type === 'P';
}

export function isDirect(row: QuickSwitcherRow): boolean {
  return row.type === 'D' || row.type === 'G';
}

/** Active conversations, most recently used first, with a stable name tie-break. */
export function rows(channels: readonly ChannelRecord[], names: ReadonlyMap<string, string>): QuickSwitcherRow[] {
  const out: QuickSwitcherRow[] = [];
  for (const channel of channels) {
    if (channel.deleteAt !== 0) continue;
    const raw = names.get(channel.id);
    if (raw === undefined) continue;
    const displayName = raw.trim();
    if (displayName === '') continue;
    out.push({ channelId: channel.id, name: displayName, slug: channel.name, type: channel.type, lastPostAt: channel.lastPostAt });
  }
  return out.sort((a, b) => {
    if (a.lastPostAt !== b.lastPostAt) return b.lastPostAt - a.lastPostAt;
    const fa = folded(a.name);
    const fb = folded(b.name);
    if (fa !== fb) return swiftStringLess(fa, fb) ? -1 : 1;
    if (a.channelId === b.channelId) return 0;
    return swiftStringLess(a.channelId, b.channelId) ? -1 : 1;
  });
}

/** Accent-insensitive matching by resolved display name or the server's channel slug. */
export function filtered(all: readonly QuickSwitcherRow[], query: string): QuickSwitcherRow[] {
  // Swift trims `.whitespaces` here (spaces and tabs, not newlines).
  const needle = folded(query.replace(/^[\p{Zs}\t]+|[\p{Zs}\t]+$/gu, ''));
  if (needle === '') return [...all];
  return all.filter((row) => folded(row.name).includes(needle) || folded(row.slug).includes(needle));
}

/**
 * Accents folded as well as case — NOT `SearchQuery.fold`, which deliberately only
 * lowercases so that search-highlight offsets stay measured against the string the row
 * draws. A switcher that made the reader type "Ángeles" with the accent would be a switcher
 * nobody uses.
 *
 * WEB: Foundation's `.diacriticInsensitive` ≈ decompose and drop the combining marks.
 */
function folded(text: string): string {
  return text.normalize('NFD').replace(/\p{Mn}/gu, '').toLowerCase();
}

/** Namespace form, for `QuickSwitcherPresentation.rows(...)` call sites. */
export const QuickSwitcherPresentation = { rows, filtered, isPrivate, isDirect } as const;
